/**
 * Forgot password page — /forgot-password
 *
 * Submits a password reset request via Better-Auth.
 * Better-Auth sends a reset email with a token link to /reset-password.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { Button } from "@better-bookkeeping/ui/button";
import { Input } from "@better-bookkeeping/ui/input";
import { Label } from "@better-bookkeeping/ui/label";
import { Alert, AlertDescription } from "@better-bookkeeping/ui/alert";
import { AlertCircle, CheckCircle } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { authClient } from "@/lib/auth-client";
import { DevEmailPreview } from "@/components/dev-email-preview";
import { buildPasswordResetCallbackURL } from "@/lib/auth-flow-redirects";
import { useRuntimeConfig } from "@/lib/runtime-config";

export const Route = createFileRoute("/forgot-password")({
  validateSearch: z.object({
    redirect_uri: z.string().optional(),
  }).parse,
  component: ForgotPasswordPage,
});

export function ForgotPasswordPage() {
  const { redirect_uri } = Route.useSearch();
  const runtimeConfig = useRuntimeConfig();
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const form = useForm({
    defaultValues: { email: "" },
    onSubmit: async ({ value }) => {
      try {
        setError(null);
        const { error: resetError } = await authClient.requestPasswordReset({
          email: value.email,
          redirectTo: buildPasswordResetCallbackURL(runtimeConfig, redirect_uri),
        });

        if (resetError) {
          if (resetError.status === 429 || resetError.message?.includes("429")) {
            setError("Too many tries. Take a breath and try again in a minute.");
          } else {
            setError("We couldn't send a reset link. Try again.");
          }
          return;
        }
        setSubmittedEmail(value.email);
      } catch (err) {
        console.error("Password reset request failed:", err);
        setError("Something went wrong. Please try again.");
      }
    },
  });

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight" data-testid="forgot-password-title">
          Reset your password
        </h2>
        <p className="text-sm text-muted-foreground">
          Tell us your email and we'll send a link to set a new one.
        </p>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="forgot-password-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {submittedEmail ? (
        <div className="space-y-3">
          <Alert data-testid="forgot-password-sent-state">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-1 text-foreground">Check your inbox.</p>
              <p className="text-sm">
                If an account exists for{" "}
                <span className="font-medium text-foreground">{submittedEmail}</span>, you'll find a
                reset link there shortly.
              </p>
            </AlertDescription>
          </Alert>
          <DevEmailPreview data-testid="forgot-password-dev-email-preview" />
          <p className="text-center text-xs text-muted-foreground">
            Didn't receive it? Check spam, or{" "}
            <button
              type="button"
              onClick={() => {
                setSubmittedEmail(null);
                form.reset();
              }}
              className="text-primary hover:underline"
              data-testid="forgot-password-try-again-button"
            >
              try a different email
            </button>
            .
          </p>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-4"
          data-testid="forgot-password-form"
        >
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <form.Field
              name="email"
              validators={{ onChange: z.string().email("Please enter a valid email") }}
              children={(field) => (
                <div>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="you@example.com"
                    autoFocus
                    required
                    data-testid="forgot-password-email-input"
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p
                      className="text-sm text-destructive mt-1"
                      data-testid="forgot-password-email-error"
                    >
                      {typeof field.state.meta.errors[0] === "string"
                        ? field.state.meta.errors[0]
                        : field.state.meta.errors[0]?.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="w-full"
                data-testid="forgot-password-submit-button"
              >
                {isSubmitting ? "Sending…" : "Send reset link"}
              </Button>
            )}
          />
        </form>
      )}

      <p className="text-center text-sm">
        <Link
          to="/login"
          search={{ redirect_uri }}
          className="text-primary hover:underline"
          data-testid="forgot-password-login-link"
        >
          ← Back to sign in
        </Link>
      </p>
    </div>
  );
}
